/* NasalStrip.uk live chat: headset launcher, help panel and hand-off to Shopify Inbox or tawk.to */
(() => {
  if (customElements.get('live-chat')) return;

  const SEEN_KEY = 'mf-chat-greeting-seen';
  const SEEN_DAYS = 3;
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };
  const mobile = () => window.matchMedia('(max-width: 749px)').matches;

  /* ---------- Shopify Inbox ----------
     Shopify Inbox renders its own launcher inside shadow roots. Shopify has changed the markup over time,
     so several known selectors are tried. Its button is hidden only once it has been found, so if Shopify
     changes it again, their button simply stays visible and still works. */
  const INBOX_BUTTONS = ['button.activator', 'button.chat-toggle', 'button[aria-controls="chat-ui"]'];
  const INBOX_HOSTS = 'inbox-online-store-chat, #ShopifyChat, #shopify-chat, #dummy-chat-button-iframe';
  const HIDE_ID = 'mf-inbox-hide';

  function allRoots() {
    const roots = [document];
    const walk = (root) => {
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) { roots.push(el.shadowRoot); walk(el.shadowRoot); }
      });
    };
    walk(document);
    return roots;
  }
  function findInboxButton() {
    for (const root of allRoots()) {
      for (const sel of INBOX_BUTTONS) {
        const el = root.querySelector(sel);
        if (el) return { el, root, sel };
      }
    }
    const frame = document.getElementById('dummy-chat-button-iframe');
    if (frame) {
      try {
        const btn = frame.contentDocument && frame.contentDocument.querySelector('button');
        if (btn) return { el: btn, root: null, sel: 'iframe' };
      } catch (e) { /* cross-origin */ }
    }
    return null;
  }
  function hideInboxButton(found) {
    if (!found) return;
    if (!found.root) {
      // Placeholder button Shopify shows in an iframe before Inbox has fully loaded
      if (!document.getElementById(`${HIDE_ID}-frame`)) {
        const st = document.createElement('style');
        st.id = `${HIDE_ID}-frame`;
        st.textContent = '#dummy-chat-button-iframe{opacity:0!important;pointer-events:none!important}';
        document.head.appendChild(st);
      }
      return;
    }
    const target = found.root === document ? document.head : found.root;
    if (target.querySelector && target.querySelector(`#${HIDE_ID}`)) return;
    const style = document.createElement('style');
    style.id = HIDE_ID;
    style.textContent = `${found.sel}{opacity:0!important;visibility:hidden!important;pointer-events:none!important;width:0!important;height:0!important;min-width:0!important;min-height:0!important;padding:0!important;margin:0!important;border:0!important;overflow:hidden!important;box-shadow:none!important}`;
    target.appendChild(style);
  }

  const providers = {
    shopify_inbox: {
      init(chat) {
        let tries = 0;
        const tick = () => {
          tries += 1;
          const found = findInboxButton();
          if (found) {
            hideInboxButton(found);
            chat.setLive(true);
            this.found = found;
          } else if (document.querySelector(INBOX_HOSTS)) {
            chat.setLive(true); // Inbox is installed but still loading
          }
          // Inbox can re-render; keep its button hidden for the first minute, then slow down
          const delay = tries < 40 ? 500 : 4000;
          if (tries < 80) setTimeout(tick, delay);
        };
        tick();
      },
      async open() {
        if (this.isOpen()) return true; // already open: clicking again would close it
        for (let i = 0; i < 16; i += 1) {
          const found = findInboxButton();
          if (found) { hideInboxButton(found); found.el.click(); return true; }
          await new Promise((r) => setTimeout(r, 250));
        }
        return false;
      },
      /** Is the Inbox chat window showing? Uses aria-expanded when Inbox provides it, otherwise looks for its visible window. */
      isOpen() {
        const found = findInboxButton();
        if (!found) return false;
        if (found.el.hasAttribute('aria-expanded')) return found.el.getAttribute('aria-expanded') === 'true';
        if (!found.root || found.root === document) return false;
        for (const el of found.root.querySelectorAll('*')) {
          if (el === found.el || el.contains(found.el)) continue;
          const r = el.getBoundingClientRect();
          if (r.width > 200 && r.height > 200) {
            const cs = getComputedStyle(el);
            if (cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0.05) return true;
          }
        }
        return false;
      },
      close() {
        const found = findInboxButton();
        if (found) found.el.click();
      }
    },

    tawk: {
      init(chat) {
        const property = chat.dataset.tawkProperty;
        if (!property) return;
        this.chat = chat;
        const api = (window.Tawk_API = window.Tawk_API || {});
        window.Tawk_LoadStart = new Date();
        if (chat.dataset.customerEmail) api.visitor = { name: chat.dataset.customerName || '', email: chat.dataset.customerEmail };
        api.customStyle = { zIndex: 2147483000 };
        const prevLoad = api.onLoad;
        api.onBeforeLoad = () => { if (!this.wantOpen) { try { api.hideWidget(); } catch (e) { /* not ready */ } } };
        api.onLoad = () => {
          this.ready = true;
          let maximized = false;
          try { maximized = api.isChatMaximized(); } catch (e) { /* older widget */ }
          if (maximized) api.showWidget(); // a conversation left open on the previous page stays open
          else if (!this.wantOpen) api.hideWidget();
          chat.setLive(true);
          if (typeof prevLoad === 'function') prevLoad();
        };
        api.onChatMinimized = () => { api.hideWidget(); chat.liveClosed(); };
        api.onChatHidden = () => chat.liveClosed();
        api.onUnreadCountChanged = (count) => chat.setUnread(Number(count) || 0);
        api.onStatusChange = (status) => chat.setAgentStatus(status);
        const load = () => {
          if (this.loading) return;
          this.loading = true;
          const s = document.createElement('script');
          s.async = true;
          s.src = `https://embed.tawk.to/${encodeURIComponent(property)}/${encodeURIComponent(chat.dataset.tawkWidget || 'default')}`;
          s.charset = 'UTF-8';
          s.setAttribute('crossorigin', '*');
          document.body.appendChild(s);
        };
        this.load = load;
        chat.setLive(true); // the button works straight away; the widget loads on demand if needed
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
        if (document.readyState === 'complete') setTimeout(() => idle(load), 2500);
        else window.addEventListener('load', () => setTimeout(() => idle(load), 2500), { once: true });
      },
      async open() {
        const api = window.Tawk_API;
        if (!this.ready) {
          this.wantOpen = true;
          if (this.load) this.load();
          for (let i = 0; i < 40 && !this.ready; i += 1) await new Promise((r) => setTimeout(r, 250));
          if (!this.ready) { this.wantOpen = false; return false; }
        }
        this.wantOpen = false;
        try { api.showWidget(); api.maximize(); return true; } catch (e) { return false; }
      },
      isOpen() {
        try {
          const api = window.Tawk_API;
          const hidden = typeof api.isChatHidden === 'function' ? api.isChatHidden() : false;
          return !!(this.ready && api.isChatMaximized() && !hidden);
        } catch (e) { return false; }
      },
      close() {
        try { window.Tawk_API.minimize(); window.Tawk_API.hideWidget(); } catch (e) { /* not loaded */ }
      }
    },

    none: { init() {}, async open() { return false; }, isOpen() { return false; }, close() {} }
  };

  class LiveChat extends HTMLElement {
    connectedCallback() {
      this.launcher = this.querySelector('[data-lc-launcher]');
      this.panel = this.querySelector('[data-lc-panel]');
      this.badge = this.querySelector('[data-lc-badge]');
      this.badgeCount = this.querySelector('[data-lc-badge-count]');
      this.thread = this.querySelector('[data-lc-thread]');
      this.body = this.querySelector('[data-lc-body]');
      this.liveBtn = this.querySelector('[data-lc-live]');
      this.status = this.querySelector('[data-lc-status]');
      this.dot = this.querySelector('[data-lc-dot]');
      this.unread = 0;
      this.greetingUnread = false;

      this.scrim = document.createElement('div');
      this.scrim.className = 'lc__scrim';
      this.scrim.hidden = true;
      this.insertBefore(this.scrim, this.panel);

      this.provider = providers[this.dataset.provider] || providers.none;

      this.launcher.addEventListener('click', () => this.onLauncher());
      this.querySelector('[data-lc-close]').addEventListener('click', () => this.close(true));
      this.scrim.addEventListener('click', () => this.close(false));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(true); });
      this.querySelectorAll('[data-lc-question]').forEach((btn) => btn.addEventListener('click', () => this.ask(btn)));
      if (this.liveBtn) this.liveBtn.addEventListener('click', () => this.openLive());

      this.updateHours();
      this.hoursTimer = setInterval(() => this.updateHours(), 60000);
      this.watchStickyCart();
      this.scheduleBadge();
      this.provider.init(this);

      if (this.hasAttribute('data-design-mode')) {
        document.addEventListener('shopify:section:select', (e) => { if (this.closest(`#shopify-section-${e.detail.sectionId}`)) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (this.closest(`#shopify-section-${e.detail.sectionId}`)) this.close(false); });
      }
    }

    disconnectedCallback() { clearInterval(this.hoursTimer); if (this.satcObserver) this.satcObserver.disconnect(); }

    get isOpen() { return this.classList.contains('is-open'); }

    onLauncher() {
      if (this.isOpen) { this.close(true); return; }
      if (this.live && this.provider.isOpen()) { this.provider.close(); return; }
      if (this.dataset.action === 'chat' && this.live) { this.markSeen(); this.openLive(); return; }
      this.open();
    }

    open() {
      if (this.isOpen) return;
      this.panel.hidden = false;
      this.scrim.hidden = !mobile();
      this.lastFocus = document.activeElement;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.launcher.setAttribute('aria-expanded', 'true');
        if (mobile()) document.body.classList.add('lc-lock');
        const first = this.querySelector('.lc__chip:not(.is-used)') || this.querySelector('[data-lc-close]');
        if (first) first.focus({ preventScroll: true });
      });
      this.markSeen();
    }

    close(restoreFocus) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.launcher.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('lc-lock');
      const done = () => { if (!this.isOpen) { this.panel.hidden = true; this.scrim.hidden = true; } };
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) done(); else setTimeout(done, 320);
      if (restoreFocus) this.launcher.focus({ preventScroll: true });
    }

    /* ----- Instant answers ----- */
    ask(btn) {
      const id = btn.dataset.lcQuestion;
      const tpl = this.querySelector(`template[data-lc-answer="${CSS.escape(id)}"]`);
      if (!tpl) return;
      this.addMessage('me', document.createTextNode(btn.textContent.trim()));
      const answer = tpl.content.cloneNode(true);
      setTimeout(() => {
        this.addMessage('them', answer);
        this.scrollDown();
      }, 180);
      btn.classList.add('is-used');
      this.scrollDown();
    }

    addMessage(who, content) {
      const msg = document.createElement('div');
      msg.className = `lc__msg lc__msg--${who}`;
      const bubble = document.createElement('div');
      bubble.className = 'lc__bubble';
      bubble.appendChild(content);
      msg.appendChild(bubble);
      this.thread.appendChild(msg);
      return msg;
    }

    scrollDown() {
      requestAnimationFrame(() => { this.body.scrollTo({ top: this.body.scrollHeight, behavior: 'smooth' }); });
    }

    /* ----- Live chat ----- */
    setLive(on) {
      if (this.live === on) return;
      this.live = on;
      this.classList.toggle('has-live', on);
      if (this.liveBtn) this.liveBtn.hidden = !on;
    }

    async openLive() {
      if (this.opening) return;
      this.opening = true;
      if (this.liveBtn) this.liveBtn.classList.add('is-loading');
      this.launcher.classList.add('is-loading');
      this.launcher.setAttribute('aria-busy', 'true');
      const ok = await this.provider.open();
      this.opening = false;
      if (this.liveBtn) this.liveBtn.classList.remove('is-loading');
      this.launcher.classList.remove('is-loading');
      this.launcher.removeAttribute('aria-busy');
      if (ok) {
        this.close(false);
        this.unread = 0;
        this.renderBadge();
        return;
      }
      // Live chat couldn't open: say so and point to the other options
      this.open();
      const alt = this.querySelector('[data-lc-alt]');
      const p = document.createElement('p');
      p.textContent = alt ? "Live chat isn't available right now. Message us on WhatsApp or by email below and we'll get back to you." : "Live chat isn't available right now. Please try again in a moment.";
      this.addMessage('them', p);
      this.scrollDown();
      this.setLive(false);
    }

    liveClosed() { if (this.provider) this.provider.opened = false; }

    setAgentStatus(status) {
      this.agentStatus = status; // 'online' | 'away' | 'offline'
      this.updateHours();
    }

    /* ----- Status line from support hours (UK time) ----- */
    updateHours() {
      if (!this.status) return;
      let online;
      if (this.agentStatus) online = this.agentStatus === 'online';
      else {
        const start = parseInt(this.dataset.hoursStart, 10);
        const end = parseInt(this.dataset.hoursEnd, 10);
        let day = ''; let hour = 0;
        try {
          const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', hour: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
          day = (parts.find((p) => p.type === 'weekday') || {}).value || '';
          hour = parseInt((parts.find((p) => p.type === 'hour') || {}).value, 10) || 0;
        } catch (e) {
          const d = new Date(); day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]; hour = d.getHours();
        }
        const days = this.dataset.days === 'everyday' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
          : this.dataset.days === 'mon_sat' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        online = days.includes(day) && hour >= start && hour < end;
      }
      this.status.textContent = online ? this.dataset.onlineText : this.dataset.awayText;
      if (this.dot) this.dot.classList.toggle('is-online', online);
    }

    /* ----- Badge ----- */
    scheduleBadge() {
      if (this.dataset.badge !== 'true') return;
      const seen = parseInt(store.get(SEEN_KEY) || '0', 10);
      if (seen && Date.now() - seen < SEEN_DAYS * 864e5) return;
      const delay = Math.max(0, parseInt(this.dataset.badgeDelay || '0', 10)) * 1000;
      setTimeout(() => {
        if (this.isOpen || this.seenThisVisit) return;
        this.greetingUnread = true;
        this.renderBadge();
      }, delay);
    }

    markSeen() {
      this.seenThisVisit = true;
      this.greetingUnread = false;
      store.set(SEEN_KEY, String(Date.now()));
      this.renderBadge();
    }

    setUnread(count) { this.unread = count; this.renderBadge(); }

    renderBadge() {
      const n = this.unread || (this.greetingUnread ? 1 : 0);
      if (!this.badge) return;
      this.badge.hidden = n === 0;
      this.badgeCount.textContent = n > 9 ? '9+' : String(n);
    }

    /* ----- Sit above the sticky add-to-cart bar ----- */
    watchStickyCart() {
      const satc = document.querySelector('sticky-atc');
      if (!satc) return;
      const root = document.documentElement;
      const update = () => {
        const on = satc.classList.contains('is-visible');
        root.classList.toggle('has-satc', on);
        if (on) {
          const inner = satc.firstElementChild || satc;
          root.style.setProperty('--satc-h', `${Math.round(inner.getBoundingClientRect().height)}px`);
        }
      };
      this.satcObserver = new MutationObserver(update);
      this.satcObserver.observe(satc, { attributes: true, attributeFilter: ['class'] });
      update();
    }
  }

  customElements.define('live-chat', LiveChat);
})();
